/** El selector de emoji de una página y la paleta de portadas. */
import { useMemo, useState, type RefObject } from 'react'

import { Flotante } from './Menu'

const EMOJIS: Record<string, string[]> = {
  Frecuentes: ['📄', '📘', '📝', '✅', '📌', '⭐', '🚀', '💡', '🔥', '🎯', '📅', '📊', '🧩', '🛠️', '📦', '🔒'],
  Trabajo: ['💼', '📁', '📂', '🗂️', '📋', '📎', '🖇️', '📏', '✏️', '🖊️', '🗒️', '📈', '📉', '🧾', '🗃️', '📮', '📬', '🏷️', '🔖', '📰'],
  Tecnología: ['💻', '🖥️', '⌨️', '🖱️', '🔌', '🔋', '📡', '🛰️', '🤖', '⚙️', '🔧', '🔩', '🧪', '🧬', '🗄️', '💾', '🧮', '🔍', '🐛', '🧠'],
  Servidor: ['🚓', '🚑', '🚒', '🚔', '🚨', '🛡️', '⚖️', '🔫', '🪪', '🏛️', '🏥', '🏢', '🚗', '🏍️', '🚁', '📻', '🎖️', '👮', '🧑‍🚒', '🧑‍⚕️'],
  Símbolos: ['❤️', '🧡', '💛', '💚', '💙', '💜', '🖤', '🤍', '⚠️', '❗', '❓', '✔️', '❌', '➕', '➖', '🔴', '🟠', '🟡', '🟢', '🔵'],
  Naturaleza: ['🌱', '🌳', '🌸', '🌞', '🌙', '⭐', '🌈', '⚡', '❄️', '🌊', '🔥', '🍀', '🍎', '☕', '🍕', '🎮', '🎵', '🎨', '🏆', '🎉'],
}

export function SelectorDeEmoji({
  ancla,
  alElegir,
  alCerrar,
  puedeQuitar,
}: {
  ancla: RefObject<HTMLElement | null>
  alElegir: (emoji: string | null) => void
  alCerrar: () => void
  puedeQuitar: boolean
}) {
  const [filtro, setFiltro] = useState('')
  const todos = useMemo(() => Object.entries(EMOJIS), [])
  const aleatorio = () => {
    const todo = Object.values(EMOJIS).flat()
    alElegir(todo[Math.floor(Math.random() * todo.length)])
  }

  return (
    <Flotante ancla={ancla} alCerrar={alCerrar} ancho={340}>
      <div className="emojis">
        <div className="emojis__cabecera">
          <input
            autoFocus
            placeholder="Filtrar por categoría…"
            value={filtro}
            onChange={(e) => setFiltro(e.target.value)}
            aria-label="Filtrar emojis"
          />
          <button className="boton boton--plano" onClick={aleatorio} title="Elegir uno al azar">
            🎲
          </button>
          {puedeQuitar && (
            <button className="boton boton--plano" onClick={() => alElegir(null)}>
              Quitar
            </button>
          )}
        </div>
        <div className="emojis__cuerpo">
          {todos
            .filter(([cat]) => cat.toLowerCase().includes(filtro.trim().toLowerCase()))
            .map(([cat, lista]) => (
              <section key={cat}>
                <h4>{cat}</h4>
                <div className="emojis__rejilla">
                  {lista.map((e) => (
                    <button key={e} onClick={() => alElegir(e)} aria-label={e}>
                      {e}
                    </button>
                  ))}
                </div>
              </section>
            ))}
        </div>
      </div>
    </Flotante>
  )
}

/** Las portadas son colores y degradados con nombre; en la base sólo se guarda `c:nombre`. */
export const PORTADAS: Record<string, string> = {
  'c:azul': 'linear-gradient(120deg, #a1c4fd 0%, #c2e9fb 100%)',
  'c:malva': 'linear-gradient(120deg, #cfd9ff 0%, #e2c9ff 100%)',
  'c:rosa': 'linear-gradient(120deg, #fbc2eb 0%, #fad0c4 100%)',
  'c:naranja': 'linear-gradient(120deg, #ffd89b 0%, #f9a875 100%)',
  'c:verde': 'linear-gradient(120deg, #d4fc79 0%, #96e6a1 100%)',
  'c:menta': 'linear-gradient(120deg, #84fab0 0%, #8fd3f4 100%)',
  'c:noche': 'linear-gradient(120deg, #434343 0%, #1a1a2e 100%)',
  'c:oceano': 'linear-gradient(120deg, #2b5876 0%, #4e4376 100%)',
  'c:arena': 'linear-gradient(120deg, #e6dccf 0%, #f3ebe0 100%)',
  'c:gris': 'linear-gradient(120deg, #d7d2cc 0%, #e8e6e3 100%)',
}

export function fondoDePortada(cover: string | null): string | undefined {
  if (!cover) return undefined
  return PORTADAS[cover]
}

export function SelectorDePortada({
  ancla,
  actual,
  alElegir,
  alCerrar,
}: {
  ancla: RefObject<HTMLElement | null>
  actual: string | null
  alElegir: (cover: string | null) => void
  alCerrar: () => void
}) {
  return (
    <Flotante ancla={ancla} alCerrar={alCerrar} ancho={320} alinear="fin">
      <div className="portadas">
        <div className="portadas__cabecera">
          <strong>Portada</strong>
          {actual && (
            <button className="boton boton--plano" onClick={() => alElegir(null)}>
              Quitar
            </button>
          )}
        </div>
        <div className="portadas__rejilla">
          {Object.entries(PORTADAS).map(([clave, fondo]) => (
            <button
              key={clave}
              className={`portadas__muestra${actual === clave ? ' portadas__muestra--activa' : ''}`}
              style={{ background: fondo }}
              onClick={() => alElegir(clave)}
              aria-label={`Portada ${clave.slice(2)}`}
            />
          ))}
        </div>
      </div>
    </Flotante>
  )
}
